import { useEffect, useId, useState, type ChangeEvent } from "react";
import { computeProjection } from "@shared/roi/engine";
import {
  buildScenarioDocument,
  parseScenarioDocument,
  type ParsedScenarioDocument,
} from "@shared/roi/document";
import { formatBreakEven, formatMoney, formatPercent } from "@shared/roi/format";
import type { ProductDetail } from "@shared/schemas/product";
import type { ScenarioDetail } from "@shared/schemas/scenario";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { TextField } from "@client/components/Field";
import { Modal } from "@client/components/Modal";
import { StatTile } from "@client/components/StatTile";
import { useImportScenario } from "@client/hooks/useScenarioVersions";
import { describeError } from "@client/lib/errorMessage";
import { SCENARIO_FILE_ACCEPT, readJsonFile } from "@client/lib/fileTransfer";

/**
 * A file becomes a scenario — the other half of the export button.
 *
 * The file is read and understood **in the browser** before anything is sent.
 * The shared engine is right here, so the dialog can show the ROI the file will
 * produce, in this product's currency, next to the name it is about to be given.
 * Importing a pricing model sight unseen — and finding out what it was only once
 * it exists — is how people end up with four scenarios called "Imported
 * scenario" and no idea which is which.
 *
 * The document posted is rebuilt from what was parsed rather than forwarded
 * verbatim, so a legacy `znms-roi-scenario` file and an edited name reach the
 * server as one canonical shape. The server parses it again regardless: this is
 * a courtesy to the user, never a substitute for validation.
 */
export function ImportScenarioDialog({
  product,
  open,
  onClose,
  onImported,
}: {
  product: ProductDetail;
  open: boolean;
  onClose: () => void;
  onImported: (scenario: ScenarioDetail) => void;
}) {
  const importScenario = useImportScenario(product.id);
  const fileInputId = useId();

  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedScenarioDocument | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [name, setName] = useState("");

  useEffect(() => {
    if (!open) return;
    setFileName(null);
    setParsed(null);
    setProblem(null);
    setName("");
    importScenario.reset();
    // `importScenario` is a stable mutation object; listing it would clear the
    // dialog on the status change that follows a successful import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // The input is cleared so picking the *same* file again after a failed
    // import still fires a change event.
    event.target.value = "";
    if (!file) return;

    setFileName(file.name);
    setParsed(null);
    importScenario.reset();

    const read = await readJsonFile(file);
    if (!read.ok) {
      setProblem(read.reason);
      return;
    }

    const document = parseScenarioDocument(read.value, {
      currencyCode: product.currencyCode,
      fallbackName: file.name.replace(/\.(roi\.)?json$/i, ""),
    });
    if (!document.ok) {
      setProblem(document.reason);
      return;
    }

    setProblem(null);
    setParsed(document.document);
    setName(document.document.name.slice(0, 120));
  }

  const projection = parsed ? computeProjection(parsed.model) : null;

  function submit() {
    if (!parsed) return;
    const document = buildScenarioDocument({
      product: { name: product.name, currencyCode: product.currencyCode },
      scenario: {
        name: name.trim() || parsed.name,
        description: parsed.description,
        id: parsed.sourceId,
        version: parsed.sourceVersion,
      },
      model: { ...parsed.model, currencyCode: product.currencyCode },
    });
    importScenario.mutate(document, { onSuccess: onImported });
  }

  return (
    <Modal
      open={open}
      title="Import a scenario"
      description={`The file becomes a new private scenario on ${product.name}, priced in ${product.currencyCode}.`}
      onClose={importScenario.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={importScenario.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!parsed || importScenario.isPending}>
            {importScenario.isPending ? "Importing…" : "Import it"}
          </Button>
        </>
      }
    >
      {/* The hint sits outside the label and is pointed at by
          `aria-describedby`, so the control is announced as "Scenario file"
          rather than as the label and the hint run together. */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={fileInputId} className="text-xs font-semibold text-ink">
          Scenario file
        </label>
        <input
          id={fileInputId}
          type="file"
          accept={SCENARIO_FILE_ACCEPT}
          aria-describedby={`${fileInputId}-hint`}
          onChange={(event) => void onPick(event)}
          disabled={importScenario.isPending}
          className="w-full rounded-md border border-line bg-chip-bg px-3 py-2 text-sm text-ink file:mr-3 file:rounded file:border-0 file:bg-azure-deep file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-on-accent"
        />
        <p id={`${fileInputId}-hint`} className="text-[11px] text-ink-soft">
          A file exported from this app, or a znms-roi-scenario file from the old ROI page.
        </p>
      </div>

      {problem && (
        <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
          {problem}
        </p>
      )}

      {parsed && projection && (
        <div className="flex flex-col gap-3 rounded-lg border border-line px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-ink">{fileName}</span>
            <Badge kind={parsed.format === "legacy" ? "warning" : "info"}>
              {parsed.format === "legacy"
                ? "Legacy ZNMS file"
                : parsed.format === "canonical"
                  ? "ROI Explorer export"
                  : "Bare model"}
            </Badge>
            {parsed.productName && parsed.productName !== product.name && (
              <span className="text-[11px] text-ink-soft">
                exported from “{parsed.productName}”
              </span>
            )}
          </div>

          <TextField
            label="Name it"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            hint="The imported scenario is private to you until you share it."
            required
          />

          <div className="grid gap-2 sm:grid-cols-3">
            <StatTile label="ROI" value={formatPercent(projection.roiPct)} />
            <StatTile
              label="Break-even"
              value={formatBreakEven(projection.breakEvenYears, parsed.model.horizonYears)}
            />
            <StatTile
              label="Net"
              tone={projection.cumNet >= 0 ? "revenue" : "cost"}
              value={formatMoney(product.currencyCode, projection.cumNet, {
                compact: true,
                signed: true,
              })}
            />
          </div>

          <p className="text-[11px] text-ink-soft">
            {parsed.model.packages.length} package
            {parsed.model.packages.length === 1 ? "" : "s"}, {parsed.model.lineItems.length} cost
            and revenue line{parsed.model.lineItems.length === 1 ? "" : "s"}, over{" "}
            {parsed.model.horizonYears} years. These figures are this file read by the same engine
            the editor uses — nothing has been created yet.
          </p>
        </div>
      )}

      {importScenario.isError && (
        <p role="alert" className="text-xs font-semibold" style={{ color: "var(--chart-crit)" }}>
          {describeError(importScenario.error)}
        </p>
      )}
    </Modal>
  );
}
